import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useFlowieStore = defineStore('flowie', () => {
    const activeConversationQueryParam = 'flowie_conversation';
    const conversations = ref({ projects: [], conversations: [] });
    const activeConversation = ref(null);
    const isGenerating = ref(false);
    const isLoading = ref(false);
    const pollTimer = ref(null);
    const hasRestoredConversationOnOpen = ref(false);
    const pendingMode = ref(null);

    const csrfToken = computed(() => document.querySelector('meta[name="csrf-token"]')?.content);

    const updateActiveConversationUrl = (id = null) => {
        if (typeof window === 'undefined') return;

        const url = new URL(window.location.href);

        if (id) {
            url.searchParams.set(activeConversationQueryParam, id);
        } else {
            url.searchParams.delete(activeConversationQueryParam);
        }

        window.history.replaceState({}, '', url.toString());
    };

    const getActiveConversationIdFromUrl = () => {
        if (typeof window === 'undefined') return null;

        return new URLSearchParams(window.location.search).get(activeConversationQueryParam);
    };

    const getConversationFromLoadedList = (id) => {
        return [
            ...conversations.value.projects,
            ...conversations.value.conversations,
        ].find((conversation) => conversation.id?.toString() === id?.toString());
    };

    const getMostRecentConversationFromLoadedList = () => {
        return [
            ...conversations.value.projects,
            ...conversations.value.conversations,
        ].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0] ?? null;
    };

    const loadConversations = async () => {
        try {
            const res = await fetch(route('bo.flowie.conversations'), {
                headers: { 'Accept': 'application/json' },
            });
            if (res.ok) {
                conversations.value = await res.json();
            }
        } catch (e) {
            console.error('Failed to load Flowie conversations', e);
        }
    };

    const loadConversation = async (id) => {
        isLoading.value = true;
        try {
            const res = await fetch(route('bo.flowie.conversation', id), {
                headers: { 'Accept': 'application/json' },
            });
            if (res.ok) {
                activeConversation.value = await res.json();
                pendingMode.value = null;
                updateActiveConversationUrl(activeConversation.value.id);
            }
        } catch (e) {
            console.error('Failed to load conversation', e);
        } finally {
            isLoading.value = false;
        }
    };

    const sendMessage = async (prompt) => {
        if (isGenerating.value || !prompt.trim()) return;

        isGenerating.value = true;
        const isFirstMessage = !activeConversation.value || (activeConversation.value.messages?.length ?? 0) === 0;
        const mode = isFirstMessage ? pendingMode.value : null;

        // Optimistically add user message
        if (!activeConversation.value) {
            activeConversation.value = {
                id: null,
                title: null,
                type: 'conversation',
                messages: [],
                tasks: [],
                progress: { total: 0, completed: 0 },
            };
        }
        activeConversation.value.messages.push({
            role: 'user',
            content: prompt,
        });

        try {
            const res = await fetch(route('bo.flowie.generate'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken.value,
                },
                body: JSON.stringify({
                    prompt,
                    conversation_id: activeConversation.value.id,
                    mode,
                }),
            });

            if (!res.ok) throw new Error('Generate request failed');

            const data = await res.json();
            activeConversation.value.id = data.conversation_id;
            pendingMode.value = null;
            updateActiveConversationUrl(data.conversation_id);

            if (data.status === 'budget_exceeded') {
                isGenerating.value = false;
                pushFailureMessage(data.status);
                return;
            }

            startPolling(data.conversation_id);
        } catch (e) {
            console.error('Failed to send message', e);
            isGenerating.value = false;
        }
    };

    // Shown locally only: the server never stores it, so it is gone on reload
    // and never becomes part of the history Flowie is later given.
    const pushFailureMessage = (status) => {
        activeConversation.value.messages.push({
            role: 'assistant',
            content: status === 'budget_exceeded'
                ? _mft('dashboard:flowie.errorBudget')
                : _mft('dashboard:flowie.errorGeneric'),
            type: 'error',
            budgetExceeded: status === 'budget_exceeded',
        });
    };

    const startPolling = (conversationId) => {
        stopPolling();
        pollTimer.value = setInterval(async () => {
            try {
                const res = await fetch(route('bo.flowie.poll', conversationId), {
                    headers: { 'Accept': 'application/json' },
                });
                if (!res.ok) return;

                const data = await res.json();

                if (data.status === 'completed') {
                    stopPolling();
                    isGenerating.value = false;

                    // Add assistant message
                    activeConversation.value.messages.push({
                        role: 'assistant',
                        content: data.message,
                        type: data.type,
                        tasks: data.tasks,
                        title: data.title,
                    });

                    // Update title
                    if (data.title && !activeConversation.value.title) {
                        activeConversation.value.title = data.title;
                    }

                    // Update tasks if action plan
                    if (data.type === 'action_plan' && Array.isArray(data.conversation_tasks)) {
                        activeConversation.value.type = 'project';
                        activeConversation.value.tasks = data.conversation_tasks;
                        activeConversation.value.progress = data.progress;
                    }

                    // Project proposal — tasks stored in message, not yet promoted
                    // No state change needed — the chat component renders the accept button

                    // Refresh sidebar
                    loadConversations();
                } else if (data.status === 'failed' || data.status === 'budget_exceeded') {
                    stopPolling();
                    isGenerating.value = false;
                    pushFailureMessage(data.status);
                } else if (data.status === 'idle') {
                    // No job is running and none will report back (e.g. its
                    // poll entry expired), so stop waiting for a reply.
                    stopPolling();
                    isGenerating.value = false;
                }
            } catch (e) {
                console.error('Polling error', e);
            }
        }, 2000);
    };

    const stopPolling = () => {
        if (pollTimer.value) {
            clearInterval(pollTimer.value);
            pollTimer.value = null;
        }
    };

    const requestGreeting = async (mode = 'chat') => {
        if (isGenerating.value) return;

        isGenerating.value = true;
        pendingMode.value = null;
        activeConversation.value = {
            id: null,
            title: null,
            type: 'conversation',
            messages: [],
            tasks: [],
            progress: { total: 0, completed: 0 },
        };

        try {
            const res = await fetch(route('bo.flowie.greet'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken.value,
                },
                body: JSON.stringify({ mode }),
            });

            if (!res.ok) throw new Error('Greet request failed');

            const data = await res.json();
            activeConversation.value.id = data.conversation_id;
            updateActiveConversationUrl(data.conversation_id);

            if (data.status === 'budget_exceeded') {
                isGenerating.value = false;
                pushFailureMessage(data.status);
                return;
            }

            startPolling(data.conversation_id);
        } catch (e) {
            console.error('Failed to request greeting', e);
            isGenerating.value = false;
        }
    };

    const newConversation = async () => {
        stopPolling();
        isGenerating.value = false;
        activeConversation.value = null;
        pendingMode.value = null;
        updateActiveConversationUrl();
    };

    const hideConversation = async (id) => {
        if (!id) return;

        try {
            const res = await fetch(route('bo.flowie.hide', id), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken.value,
                },
            });

            if (!res.ok) throw new Error('Hide request failed');

            if (activeConversation.value?.id === id) {
                stopPolling();
                isGenerating.value = false;
                activeConversation.value = null;
                pendingMode.value = null;
                updateActiveConversationUrl();
            }

            await loadConversations();
        } catch (e) {
            console.error('Failed to hide conversation', e);
        }
    };

    const setPendingMode = (mode = null) => {
        pendingMode.value = pendingMode.value === mode ? null : mode;
    };

    const toggleTask = async (taskId) => {
        if (!activeConversation.value?.id) return;

        // Optimistic update
        const tasks = activeConversation.value.tasks || [];
        const task = tasks.find(t => t.id === taskId);
        if (task) {
            task.completed = !task.completed;
            const completed = tasks.filter(t => t.completed).length;
            activeConversation.value.progress = { total: tasks.length, completed };
        }

        try {
            const res = await fetch(route('bo.flowie.toggleTask', activeConversation.value.id), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken.value,
                },
                body: JSON.stringify({ task_id: taskId }),
            });

            if (res.ok) {
                const data = await res.json();
                activeConversation.value.tasks = data.tasks;
                activeConversation.value.progress = data.progress;
                loadConversations();
            }
        } catch (e) {
            console.error('Failed to toggle task', e);
        }
    };

    const promoteToProject = async () => {
        if (!activeConversation.value?.id) return;

        try {
            const res = await fetch(route('bo.flowie.promote', activeConversation.value.id), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken.value,
                },
            });

            if (!res.ok) throw new Error('Promote request failed');

            const data = await res.json();
            activeConversation.value.type = data.type;
            activeConversation.value.tasks = data.tasks;
            activeConversation.value.progress = data.progress;
            if (data.title) {
                activeConversation.value.title = data.title;
            }

            loadConversations();
        } catch (e) {
            console.error('Failed to promote to project', e);
        }
    };

    // A conversation seeded server-side (e.g. from the /discover signup flow)
    // can be opened while its FlowieGenerateJob is still running — the last
    // message is then the user's, with no reply yet. Resume polling so the
    // reply streams in instead of the chat looking stuck.
    const resumePollingIfAwaitingReply = () => {
        const messages = activeConversation.value?.messages ?? [];

        // FlowieGenerateJob appends messages only on completion, so an
        // in-flight conversation is either empty or ends on a user message.
        // Empty conversations only persist while a job runs (or after a dead
        // job whose failure status the poll endpoint reports), so polling
        // here is safe and self-terminating.
        if (!messages.length || messages[messages.length - 1].role === 'user') {
            isGenerating.value = true;
            startPolling(activeConversation.value.id);
        }
    };

    const restoreConversationOnOpen = async () => {
        if (hasRestoredConversationOnOpen.value) return;

        hasRestoredConversationOnOpen.value = true;

        const conversationId = getActiveConversationIdFromUrl();

        if (conversationId && activeConversation.value?.id?.toString() !== conversationId) {
            const conversation = getConversationFromLoadedList(conversationId);

            if (conversation) {
                await loadConversation(conversation.id);
                resumePollingIfAwaitingReply();
                return;
            }

            updateActiveConversationUrl();
        }

        if (activeConversation.value) {
            return;
        }

        const latestConversation = getMostRecentConversationFromLoadedList();

        if (!latestConversation) {
            return;
        }

        await loadConversation(latestConversation.id);
        resumePollingIfAwaitingReply();
    };

    const restoreConversationFromUrl = restoreConversationOnOpen;

    return {
        conversations,
        activeConversation,
        isGenerating,
        isLoading,
        pendingMode,
        loadConversations,
        loadConversation,
        sendMessage,
        requestGreeting,
        startPolling,
        stopPolling,
        newConversation,
        hideConversation,
        setPendingMode,
        toggleTask,
        promoteToProject,
        restoreConversationOnOpen,
        restoreConversationFromUrl,
    };
});
